import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { AppTextService } from '@fi-shared/app-text';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { WidgetService } from '@fi-app/widget/widget.service';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import {
    AppearanceModuleSideListOptions,
    IAppearanceModuleSideListOption,
} from '../../../apeparance-module-side-list-options';
import { ModuleType } from '@fi-shared/types/module-type';
import { SortByProperty } from '@fi-shared/utils/sort-by-property';
import { WidgetContentModel } from '@fi-app/widget/model/widget-content.model';
import { WidgetModel } from '@fi-app/widget/model/widget.model';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { IContentLanguage } from '@fi-app/shared/fi-content-language-shared/fi-content-language-shared';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { createMap } from '@fi-shared/utils/create-map';
import { IAppearanceSidebarStateModel } from '@fi-app/appearance/appearance';
import { FiFormatDateService } from '@fi-shared/fi-format-date/fi-format-date.service';

@Component({
    selector: 'fi-appearance-sidebar-edit',
    templateUrl: './sidebar-edit.component.html',
    styleUrls: ['./sidebar-edit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-appearance-sidebar-edit' },
    standalone: false,
})
export class FiAppearanceSidebarEditComponent implements OnInit {
    @Input() siteDesignId: number;
    @Input('widgets') Widgets: WidgetModel[];
    @Input() appearanceSidebarStateModel: IAppearanceSidebarStateModel;

    WidgetsMap: { [prop: number]: WidgetModel } = {};
    moduleWidgetMap = {};
    initialWidgetsIds = [];
    defaultLanguage: IContentLanguage;
    isSaving = false;
    widgetsMap: { [prop: number]: WidgetModel };
    showTrashBin = false;
    selectedWidgetControl: WidgetContentModel;
    sideListOptions: IAppearanceModuleSideListOption[] = [];
    selectedSideListOption: IAppearanceModuleSideListOption;
    editTabs: EditTabsModel;

    constructor(
        private contentLanguagesService: ContentLanguagesService,
        private appTextService: AppTextService,
        private loadingService: LoadingService,
        private widgetService: WidgetService,
        private saveChangesWarningService: SaveChangesWarningService,
        private userDataService: FiUserDataService,
        private stateService: FiStateService,
        private fiFormatDateService: FiFormatDateService,
    ) {}

    ngOnInit() {
        this.editTabs = new EditTabsModel(
            this.appTextService.get('common.appearance'),
            this.appTextService.get('common.cancel'),
        );
        this.sideListOptions = AppearanceModuleSideListOptions.filter((item) => item.moduleType !== ModuleType.Core);

        this.defaultLanguage = this.contentLanguagesService.GetDefaultLanguage();
        this.widgetsMap = createMap(this.Widgets, 'Id');
        this.selectedSideListOption = this.sideListOptions.find(
            (item) => item.previewTemplateName === this.appearanceSidebarStateModel.modulePreviewTemplateName,
        );

        this.sideListOptions.forEach((option: IAppearanceModuleSideListOption) => {
            this.moduleWidgetMap[option.moduleType] = [];
        });

        this.Widgets.forEach((widget: WidgetModel) => {
            this.WidgetsMap[widget.WidgetType] = widget;
            widget.WidgetModuleContentCollection.forEach((content: WidgetContentModel) => {
                content.InitializeWidgetContentLanguageLabels(this.contentLanguagesService.GetContentLanguages());
                this.addWidgetContentInModuleMap(content);
            });
        });

        Object.keys(this.moduleWidgetMap).forEach((key: string) => {
            this.moduleWidgetMap[key] = SortByProperty(this.moduleWidgetMap[key], 'SortOrder', true);
        });

        this.saveChangesWarningService.start(() => this.getModelData());
    }

    getControlHeaderTitle() {
        const control = this.selectedWidgetControl;
        if (!control) return '';

        const names = control.Settings.HeaderNames;

        for (var i in names) {
            if (names.hasOwnProperty(i) && +i === this.defaultLanguage.Id && names[i]) {
                return names[i];
            }
        }

        if (this.widgetsMap.hasOwnProperty(control.WidgetId)) {
            // todo TextId is undefined
            return this.appTextService.get(this.widgetsMap[control.WidgetId].TextId);
        }
    }

    getLanguageTextToDisplay(languageId: number): string {
        const item: IContentLanguage = this.contentLanguagesService
            .GetContentLanguages()
            .find((item: IContentLanguage) => item.Id == languageId);

        if (item) {
            return item.Name;
        }
    }

    widgetDragStart() {
        this.showTrashBin = true;
    }

    widgetDragEnd() {
        this.showTrashBin = false;
        this.selectedWidgetControl = null;
    }

    save() {
        if (!this.isSaving) {
            const loading = this.loadingService.startLoading();
            var widgetsToSave = [];
            var widgetsToSaveIds = [];
            var widgetsToDelete = [];

            Object.keys(this.moduleWidgetMap).forEach((key) => {
                const moduleContents = this.moduleWidgetMap[key];
                var sortOrder = 1;

                moduleContents.forEach((item) => {
                    var itemToSave = JSON.parse(JSON.stringify(item));
                    itemToSave.SortOrder = sortOrder;
                    sortOrder++;
                    itemToSave.Settings = JSON.stringify(itemToSave.Settings);
                    widgetsToSave.push(itemToSave);
                    if (itemToSave.Id > 0) {
                        widgetsToSaveIds.push(itemToSave.Id);
                    }
                });
            });

            this.initialWidgetsIds.forEach((id) => {
                if (widgetsToSaveIds.indexOf(id) == -1) {
                    widgetsToDelete.push(id);
                }
            });

            var promise = this.widgetService.saveWidget(widgetsToSave, this.siteDesignId);

            promise.then(
                (data) => {
                    if (data && widgetsToDelete.length > 0) {
                        var deletePromise = this.widgetService.DeleteWidgetContent(widgetsToDelete);
                        deletePromise.then(() => {
                            loading();
                            this.saveChangesWarningService.stop();
                            this.stateService.go(FiStateNames.OtherSettings.SiteDesign.BrandEdit, {
                                Id: this.siteDesignId,
                            });
                        });
                    } else {
                        loading();
                        this.saveChangesWarningService.stop();
                        this.stateService.go(FiStateNames.OtherSettings.SiteDesign.BrandEdit, {
                            Id: this.siteDesignId,
                        });
                    }
                },
                () => loading(),
            );
        }

        this.isSaving = true;
    }

    selectSideListOption(sideListOption) {
        this.appearanceSidebarStateModel.modulePreviewTemplateName = sideListOption.previewTemplateName;
        this.selectedSideListOption = sideListOption;
    }

    isSideListOptionSelected(sideListOption) {
        return sideListOption == this.selectedSideListOption;
    }

    // end: options side block

    addWidgetContentInModuleMap(content) {
        this.initialWidgetsIds.push(content.Id);
        if (!this.moduleWidgetMap.hasOwnProperty(content.ModuleType)) this.moduleWidgetMap[content.ModuleType] = [];
        this.moduleWidgetMap[content.ModuleType].push(content);
    }

    onWidgetMoved(widgetsList, $index) {
        widgetsList.splice($index, 1);
    }

    onWidgetDrop(item, type, index) {
        // on drop control we need re-create original placeholder because of dnd-list copy as plain object
        // if a widget dropped from sidebar - need to create a new placeholder content
        if (type == 'control') {
            return new WidgetContentModel(item);
        } else if (type == 'widget') {
            // on adding a new control - add his widget to a storage
            var widgetContent = new WidgetContentModel(this.contentLanguagesService.GetContentLanguages(), {
                WidgetId: item.Id,
                ModuleType: this.selectedSideListOption.moduleType,
                WidgetSetting: item.Settings,
                SortOrder: index,
                WidgetType: item.WidgetType,
                SiteDesignId: this.siteDesignId,
            });
            widgetContent.InitializeWidgetContentLanguageLabels(this.contentLanguagesService.GetContentLanguages());
            return widgetContent;
        } else {
            return false;
        }
    }

    selectWidgetControl(widgetControl: WidgetContentModel) {
        this.selectedWidgetControl = widgetControl;
    }

    getSelectedWidgetControl() {
        return this.selectedWidgetControl;
    }

    finishedEdit() {
        this.selectedWidgetControl.Names.forEach((Name) => {
            if (this.defaultLanguage.Id == Name.ContentLanguageId) {
                this.selectedWidgetControl.Name = Name.Value;
            }
        });

        this.selectedWidgetControl.Settings.DateChanged = new Date();
        this.selectedWidgetControl = null;
    }

    getModelData() {
        const items: WidgetModel[] = [];
        Object.keys(this.moduleWidgetMap).forEach((key: string) => {
            const widgetItem = this.moduleWidgetMap[key];
            widgetItem.forEach((item) =>
                items.push(
                    new WidgetModel(this.contentLanguagesService.GetContentLanguages(), this.fiFormatDateService, item),
                ),
            );
        });

        return {
            items: JSON.stringify(items.map((item) => item.Settings)),
        };
    }
}
